import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { FC, Fragment, useEffect } from 'react'
import { FloorPlanPreview2D, FloorPlanPreview3D } from 'components/common/FloorPlanControls'
import { FloorPlanRenderType, FloorPlanSelectionType, useFloorPlanConfig } from './FloorPlanConfigStep.context'
import { usePurchaseFlowConfig, usePurchaseFlowProducts } from 'components/pages/PurchaseFlow/_main/contexts'

import Box from '@mui/material/Box'
import { FloorPlanDesign } from '../FloorPlanDesign'
import { FloorPlanLayout } from '../FloorPlanLayout'
import { FloorPlanRenderTypeController } from '../FloorPlanRenderType'
import { FloorPlanTemplateTypeSelection } from '../FloorPlanTemplateTypeSelection'
import { MUIInputField } from 'components/common/MUIInputField'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { ShootingCategory } from 'constants/product'
import Stack from '@mui/material/Stack'
import { StepWrapper } from '../../../common/StepWrapper'
import TransitionAppear from 'components/common/TransitionAppear/TransitionAppear'
import { useTranslation } from 'react-i18next'

/**
 * Controller for handling configuration of floor plan details and such things
 *
 * @example <FloorPlanConfigStepController />
 */
export const FloorPlanConfigStepController: FC = () => {
  const { t } = useTranslation(['purchase_flow'])
  const { selectedCategory } = usePurchaseFlowConfig()

  const {
    currentFloorPlanRenderType,
    floorPlanTemplateType,
    comment,
    setComment,
    selectedColor,
    selectedStyle,
    selectedOrientation,
    selectedTemplateId,
    showFixtures,
    showFurniture,
    showRoomNames,
    showScalebar,
    showCompass,
    showDisclaimer,
    isActiveLogo,
    companyLogo,
    isActiveDimensions,
    showDimensionChain,
    showRoomArea,
  } = useFloorPlanConfig()

  const previewOverlays = {
    showFixtures,
    showFurniture,
    showRoomNames,
    showScalebar,
    showCompass,
    showDisclaimer,
    showLogo: isActiveLogo && !!companyLogo,
    isActiveDimensions,
    showDimensionChain,
    showRoomArea,
  }

  const {
    selectedKinds,
    selectedProductTypes,
    selectedProducts,
  } = usePurchaseFlowProducts()

  useEffect(() => {
    logAnalyticsEvent(AnalyticsEvent.ENTER_ORDER_FLOOR_PLAN_PAGE_SCREEN, {
      productTypes: Array.from(selectedProductTypes),
      productKinds: Array.from(selectedKinds),
      products: Object.values(selectedProducts),
      category: selectedCategory,
    })

    // Only trigger when the component mounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <TransitionAppear visible>
      <StepWrapper title={t('floor_plan_step.step_title')} subtitle={t('floor_plan_step.step_subtitle')}>
        <Stack
          gap={4}
          width="100%"
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
        >

          {/** FLOOR PLAN PREVIEWS */}
          <Box
            top="-0.4rem" // So it aligns with the Cart component
            height="100%"
            flex="1 0 300px"
            position="sticky"
          >
            {currentFloorPlanRenderType === FloorPlanRenderType.RENDER_2D &&
              <FloorPlanPreview2D {...previewOverlays} color={selectedColor} templateId={selectedTemplateId} />
            }

            {currentFloorPlanRenderType === FloorPlanRenderType.RENDER_3D &&
              <FloorPlanPreview3D {...previewOverlays} style={selectedStyle} orientation={selectedOrientation} />
            }
          </Box>

          {/** SELECT OR CREATE TEMPLATES */}
          <Stack flex="1 1 75%" gap={2}>

            {/** Select whether to display custom template or create one from scratch */}
            {floorPlanTemplateType !== FloorPlanSelectionType.CUSTOM &&
              <FloorPlanTemplateTypeSelection />
            }

            {/** If template type is NEW_TEMPLATE display the render type selection */}
            {floorPlanTemplateType === FloorPlanSelectionType.CUSTOM &&
              <FloorPlanRenderTypeController />
            }

            {/** Floor Plan configuration inputs */}
            {floorPlanTemplateType &&
              <Fragment>

                <FloorPlanDesign />

                {currentFloorPlanRenderType === FloorPlanRenderType.RENDER_2D && <FloorPlanLayout />}

                <SectionedBorderBox title={t('floor_plan_step.comment_section_title')}>
                  <MUIInputField
                    isMultiline
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    label={t(selectedCategory !== ShootingCategory.REAL_ESTATE ? 'floor_plan_step.comment_label_editing' : 'floor_plan_step.comment_label')}
                  />
                </SectionedBorderBox>

              </Fragment>
            }

          </Stack>

        </Stack>
      </StepWrapper>
    </TransitionAppear>
  )
}
